import { useEffect, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ChildInfo, getChildren } from '../../../api/childrenApi';
import { ChevronRightIcon, PlusIcon } from '../../../components/icons';
import AddChildModal from '../../../components/parent/children/AddChildModal';
import { useToast } from '../../../context/ToastContext';
import { calculateAge } from '../../../utils/calendar';

function calculateAgeFromString(birthDate: string): number {
  return calculateAge(new Date(birthDate));
}

function formatBirthDate(birthDate: string): string {
  const d = new Date(birthDate);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  return `${day}.${month}.${d.getFullYear()}`;
}

function formatBalance(balance: number): string {
  const abs = Math.abs(balance).toFixed(2).replace('.', ',');
  return balance < 0 ? `- ${abs} PLN` : `${abs} PLN`;
}

function getInitials(firstName: string, lastName: string): string {
  return `${firstName[0] ?? ''}${lastName[0] ?? ''}`.toUpperCase();
}

export default function ChildrenView() {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [children, setChildren] = useState<ChildInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    getChildren()
      .then((res) => setChildren(res.rows))
      .finally(() => setLoading(false));
  }, []);

  function handleChildAdded(fullName: string) {
    showToast(t('addChild.successToast', { name: fullName }));
    getChildren().then((res) => setChildren(res.rows));
  }

  return (
    <Box sx={(theme) => ({ p: 4, width: '100%', flex: 1, backgroundColor: theme.app.mutedBackground })}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 3 }}>
        <Typography variant="h4">{t('sidebar.children')}</Typography>
        {!loading && (
          <Typography variant="h4" color="text.secondary">
            {children.length}
          </Typography>
        )}
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}>
          <CircularProgress size={32} />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <AddChildRow onClick={() => setModalOpen(true)} />
          {children.map((child) => (
            <ChildRow key={child.id} child={child} />
          ))}
        </Box>
      )}

      <AddChildModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onChildAdded={handleChildAdded}
      />
    </Box>
  );
}

function AddChildRow({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation();
  return (
    <Box
      onClick={onClick}
      sx={(theme) => ({
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        px: 3,
        py: 2.5,
        borderRadius: 2,
        border: `1px solid ${theme.app.border}`,
        backgroundColor: theme.app.accentSurface,
        cursor: 'pointer',
        userSelect: 'none',
        '&:hover': { opacity: 0.85 },
        transition: 'opacity 0.15s',
      })}
    >
      <PlusIcon size={20} />
      <Typography variant="body1" sx={{ fontWeight: 500 }}>
        {t('children.addChild')}
      </Typography>
    </Box>
  );
}

function VDivider() {
  return (
    <Box
      sx={(theme) => ({
        width: '1px',
        alignSelf: 'stretch',
        backgroundColor: theme.app.border,
        flexShrink: 0,
        mx: 1,
      })}
    />
  );
}

function ChildRow({ child }: { child: ChildInfo }) {
  const { t } = useTranslation();
  const initials = getInitials(child.firstName, child.lastName);
  const isMale = child.sex === 'MALE';
  const age = calculateAgeFromString(child.birthDate);
  const birthDateFormatted = formatBirthDate(child.birthDate);
  const hasOrg = child.organizations.length > 0;
  const firstOrg = child.organizations[0]?.name ?? '-';
  const extraOrgs = child.organizations.length > 1 ? child.organizations.length - 1 : 0;
  const balance = -(child.pendingLiabilitiesAmount ?? 0);
  const balanceFormatted = formatBalance(balance);
  const isNegative = balance < 0;

  return (
    <Box
      sx={(theme) => ({
        display: 'flex',
        alignItems: 'center',
        px: 3,
        py: 2,
        borderRadius: 2,
        border: `1px solid ${theme.app.border}`,
        backgroundColor: theme.app.card,
      })}
    >
      {/* Avatar */}
      <Box
        sx={(theme) => ({
          width: 44,
          height: 44,
          borderRadius: '50%',
          backgroundColor: isMale ? theme.app.infoSurface : theme.app.destructiveSurface,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          mr: 2,
        })}
      >
        <Typography
          variant="caption"
          sx={(theme) => ({ fontWeight: 700})}
        >
          {initials}
        </Typography>
      </Box>

      {/* Name + age */}
      <Box sx={{ flex: 1, mr: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {child.firstName} {child.lastName}
        </Typography>
        <Typography variant="caption" color="text.secondary">
            <Box component="span" sx={{ fontWeight: 600}}>
                {age} {t('children.years')}{' '}
            </Box>
            <Box component="span" sx={(theme) => ({ color: theme.app.mutedForeground })}>
                ({birthDateFormatted})
            </Box>
        </Typography>
      </Box>

      {hasOrg && (
        <>
          <VDivider />

          {/* Organization */}
          <Box sx={{ flex: 1, px: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
              {t('children.organizationLabel')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {firstOrg}
              </Typography>
              {extraOrgs > 0 && (
                <Box
                  sx={(theme) => ({
                    px: 1,
                    py: 0.25,
                    borderRadius: 99,
                    backgroundColor: theme.app.secondary,
                  })}
                >
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    +{extraOrgs}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          {/* Class */}
          <Box sx={{ flex: '0 0 80px', px: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
              {t('children.classLabel')}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              1A
            </Typography>
          </Box>

          <VDivider />

          {/* Balance */}
          <Box sx={{ flex: 1, px: 2, textAlign: 'right' }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
              {t('children.balanceLabel')}
            </Typography>
            <Typography
              variant="body2"
              sx={(theme) => ({
                fontWeight: 600,
                color: isNegative ? theme.app.destructive : 'text.primary',
              })}
            >
              {balanceFormatted}
            </Typography>
          </Box>

          <VDivider />
        </>
      )}

      {/* Chevron */}
      <Box
        sx={(theme) => ({
          width: 32,
          height: 32,
          borderRadius: '50%',
          backgroundColor: theme.app.secondary,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          ml: 1,
          color: theme.app.mutedForeground,
        })}
      >
        <ChevronRightIcon size={16} />
      </Box>
    </Box>
  );
}
